import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "../../pages/ProductSelect.scss";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, openCart } from "../../store/cartSlice";
import { getImageFromDB, saveImageToDB } from "../../utils/indexedDB";
import { selectUploadedImageIds, setCustomizerStep } from "../../store/customizerSlice";
import ProductDetailsModal from "./ProductDetailsModal";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { Oval } from 'react-loader-spinner';
import { useAffiliate } from "../../hooks/useAffiliate";
import { useGetProductsQuery } from "../../store/apiSlice";
import ProductBox from "./ProductBox";
import useProductModal from "../../hooks/useProductModal";
import { fbqTrack } from "../../utils/eventsTracking/fbq";
import { gtagEvent } from "../../utils/eventsTracking/gtag";
import { trackCustomEvent } from "../../utils/eventsTracking/trackCustomEvent";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";


const productsGrouped = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
};


const IlmdProductSelect = ({ isMobile }) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { affiliate, isLoading: isLoadingAffiliate } = useAffiliate();
  const { data: products, isLoading } = useGetProductsQuery();
  const [finalImages, setFinalImages] = useState({});
  const canvasRef = useRef(null);
  const [subscriptions, setSubscriptions] = useState({});
  const [selectedPetType, setSelectedPetType] = useState(() => localStorage.getItem("petType"));
  const uploadedImageIds = useSelector(selectUploadedImageIds);
  const [allImages, setAllImages] = useState([]);


  const {
    isOpen: isProductModalActive,
    modalData: productDetailsModalFor,
    openModal,
    closeModal
  } = useProductModal();

  const toggleSubscription = (productId) => {
    setSubscriptions((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  useEffect(() => {
    if (!selectedPetType) {
      navigate("/customizer");
    }
    dispatch(setCustomizerStep(2));
  }, []);

  useEffect(() => {
    if (!products || isLoading) return;

    const applyPrintToProducts = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      let updatedImages = {};

      // Get resultImageId from localStorage
      const resultImageId = localStorage.getItem("resultImageId");

      // Check if resultImage exists in IndexedDB to continue
      const resultImageBlob = await getImageFromDB("resultImages", resultImageId);
      if (!resultImageBlob) {
        return;
      }

      for (const product of products) {
        const onProductImage = await getImageFromDB("onProductImages", null, { productId: product.id, resultImageId });

        if (onProductImage) {
          updatedImages[product.id] = URL.createObjectURL(onProductImage);
          continue;
        }

        const mockupImageSrc = product.image;
        const mockupImage = new Image();
        mockupImage.src = mockupImageSrc;
        mockupImage.crossOrigin = "anonymous";

        await new Promise((resolve) => {
          mockupImage.onload = async () => {
            const imageBlob = await getImageFromDB("resultImages", resultImageId);

            if (!imageBlob) return resolve();

            const customizedImage = new Image();
            customizedImage.src = URL.createObjectURL(imageBlob);
            customizedImage.crossOrigin = "anonymous";

            customizedImage.onload = () => {
              const canvasWidth = mockupImage.width;
              const canvasHeight = mockupImage.height;
              canvas.width = canvasWidth;
              canvas.height = canvasHeight;

              ctx.clearRect(0, 0, canvasWidth, canvasHeight);
              ctx.globalCompositeOperation = "source-over";
              ctx.drawImage(mockupImage, 0, 0, canvasWidth, canvasHeight);

              const overlayWidth = canvasWidth * 0.36;
              const overlayHeight = (overlayWidth * customizedImage.height) / customizedImage.width;
              const overlayX = (canvasWidth - overlayWidth) / 2;
              const overlayY = (canvasHeight - overlayHeight) / 1.65;
              ctx.drawImage(customizedImage, overlayX, overlayY, overlayWidth, overlayHeight);
              ctx.globalCompositeOperation = "source-over";

              // Convert canvas to blob and save in IndexedDB
              canvas.toBlob(async (finalBlob) => {
                if (!finalBlob) return resolve();
                await saveImageToDB("onProductImages", `${product.id}_${resultImageId}`, finalBlob, product.id, resultImageId);
                updatedImages[product.id] = URL.createObjectURL(finalBlob);
                resolve();
              }, "image/png");
            };
          };
        });
      }

      setFinalImages(updatedImages);
    };

    applyPrintToProducts();
  }, [products, isLoading]);



  // Filters
  const filteredProducts = (products || [])
    .filter((product) => product.petType.toLowerCase() === 'dog');

  const handleAddToCart = async (product, isSubscribed = false, quantity = 1) => {
    const resultImageId = localStorage.getItem("resultImageId");

    closeModal();

    dispatch(addToCart({
      ...product,
      quantity,
      subscription: isSubscribed,
      imageId: resultImageId,
      uploadedImageIds
    }));

    dispatch(openCart());

    // Events for marketing
    trackCustomEvent('added_to_cart', {
      sku: product.sku,
      price: product.price,
      title: product.title,
      flavor: product.flavor,
      quantity: quantity,
      subscription: isSubscribed,
      context: 'partner\'s product',
      campaign: 'ILMD',
    }, affiliate?.code || null);
    fbqTrack('AddToCart', {
      contents: [
        {
          id: product.sku,
          quantity: quantity,
          item_price: product.price,
          title: product.title,
          flavor: product.flavor,
          subscription: isSubscribed
        }
      ],
      content_ids: [product.sku],
      content_type: 'product',
      content_name: product.title,
      content_category: 'partner\'s product',
      value: product.price * quantity,
      currency: 'USD',
      context: 'partner\'s product'
    });
    gtagEvent('add_to_cart', {
      currency: 'USD',
      value: product.price * quantity,
      items: [
        {
          item_id: product.sku,
          item_name: product.title,
          item_category: 'customized product',
          quantity: quantity,
          price: product.price,
          flavor: product.flavor,
          subscription: isSubscribed
        }
      ]
    });
  };



  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen min-h-screen">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    );
  }

  return (
    <div className="product-boxes home-slider-container">
      {filteredProducts?.length > 0 ?
        <div className="product-boxes home-slider-container">
          <Swiper
            modules={[Pagination]}
            pagination={{ el: ".slider-pagination", clickable: true }}
            slidesOffsetAfter={0}
            slidesPerGroup={1}
            breakpoints={{
              300: { slidesPerView: 1 },
              700: { slidesPerView: 2 },
              1000: { slidesPerView: 3 },
            }}
          >
            {productsGrouped(filteredProducts, 2).map((productChunk, index) => (
              <SwiperSlide key={`slide-${index}`}>
                {productChunk.map((product) => (
                  <ProductBox
                    key={product.id}
                    product={product}
                    finalImages={finalImages}
                    subscriptions={subscriptions}
                    openModal={openModal}
                    toggleSubscription={toggleSubscription}
                    handleAddToCart={handleAddToCart}
                  />
                ))}
              </SwiperSlide>
            ))}
            <div className="slider-pagination" />
          </Swiper>
        </div>
        :
        <p className="text-gray-500 text-center col-span-full color-swap">No products found.</p>
      }

      <ProductDetailsModal
        isOpen={isProductModalActive}
        onClose={closeModal}
        product={productDetailsModalFor}
        handleAddToCart={handleAddToCart}
        allImages={allImages}
        setAllImages={setAllImages}
        image={
          productDetailsModalFor
            ? finalImages[productDetailsModalFor.id] || productDetailsModalFor.image
            : ""
        }
      />

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};




IlmdProductSelect.propTypes = {
  isMobile: PropTypes.bool.isRequired
};

export default IlmdProductSelect;
